import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Fragment, type ReactNode } from 'react';
import useMoney from '../../../../hooks/useMoney';
import LangEnum from '../../../../lang/LangEnum';
import type { ActivityFeedRowDTO } from '../../../../services/modules/activityFeed/activityFeed.types';
import { formatDate, formatTime } from '../../../../utils/dates';
import useTargetValueFormatter from '../../../target/hooks/useTargetValueFormatter';
import { useTranslation } from '../../../translation/TranslationContext';
import type {
  ActivityFeedAttainment,
  ActivityFeedOrderTotals,
  ActivityFeedPhraseDetail,
  ActivityFeedPhrase as ActivityFeedPhraseType,
} from '../../utils/activityFeedPhrase.types';
import {
  resolveActivityFeedOrderCode,
  resolveActivityFeedSnapshotKind,
} from '../../utils/activityFeedSnapshot.utils';
import ActivityFeedChildOrders from '../ActivityFeedChildOrders';
import ActivityFeedPhrase from '../ActivityFeedPhrase';
import ActivityFeedShipmentTooltip from '../ActivityFeedShipmentTooltip';
import ActivityFeedSnapshotDownload from '../ActivityFeedSnapshotDownload';
import ActivityFeedTrackingLink from '../ActivityFeedTrackingLink';
import FeedItem from '../FeedItem/FeedItem';

interface ActivityFeedRowProps {
  row: ActivityFeedRowDTO;
  phrase: ActivityFeedPhraseType;
  testId?: string;
  snapshotTestId?: string;
  trackingTestId?: string;
}

interface MetadataSegment {
  key: string;
  content: ReactNode;
}

const METADATA_SEPARATOR = ' · ';

const CONVERSION_FRACTION_DIGITS = 2;

const ActivityFeedRow = ({
  row,
  phrase,
  testId,
  snapshotTestId,
  trackingTestId,
}: ActivityFeedRowProps) => {
  const { translate, locale } = useTranslation();
  const { formatMoney } = useMoney();

  const { attainment, childOrders, details, lossReason, totals } = phrase;
  const {
    formatValue: formatTargetValue,
    unitSuffix,
    isMoney,
  } = useTargetValueFormatter(attainment?.unit);
  const snapshotKind = resolveActivityFeedSnapshotKind(row);

  const formatDetail = (detail: ActivityFeedPhraseDetail) =>
    'text' in detail ? detail.text : translate(detail.key, detail.values);

  const formatTotals = ({ totalValue, totalItems, totalPieces }: ActivityFeedOrderTotals) =>
    [
      formatMoney(totalValue),
      translate(LangEnum.GLOBAL_ITEM, { quantity: totalItems }),
      translate(LangEnum.GLOBAL_PIECE, { quantity: totalPieces }),
    ].join(METADATA_SEPARATOR);

  const formatConversion = (conversion: number) =>
    translate(LangEnum.ACTIVITY_FEED_TARGET_CONVERSION, {
      conversion: conversion.toLocaleString(locale, {
        minimumFractionDigits: CONVERSION_FRACTION_DIGITS,
        maximumFractionDigits: CONVERSION_FRACTION_DIGITS,
      }),
    });

  const withUnitSuffix = (value: string) => (isMoney ? value : `${value} ${unitSuffix}`);

  const formatAttainment = ({
    attainedTotal,
    plannedTotal,
    conversion,
  }: ActivityFeedAttainment) => {
    const planned = formatTargetValue(plannedTotal);

    if (attainedTotal === null) {
      return withUnitSuffix(planned);
    }

    const attained = withUnitSuffix(
      translate(LangEnum.ACTIVITY_FEED_TARGET_ATTAINMENT, {
        attained: formatTargetValue(attainedTotal),
        planned,
      }),
    );

    if (conversion === null) {
      return attained;
    }

    return [attained, formatConversion(conversion)].join(METADATA_SEPARATOR);
  };

  const metadataSegments: MetadataSegment[] = [
    { key: 'attainment', content: attainment ? formatAttainment(attainment) : null },
    {
      key: 'lossReason',
      content: lossReason
        ? translate(LangEnum.ACTIVITY_FEED_DETAIL_REASON, { reason: translate(lossReason) })
        : null,
    },
    { key: 'totals', content: totals ? formatTotals(totals) : null },
    { key: 'details', content: details.map(formatDetail).join(METADATA_SEPARATOR) },
    {
      key: 'childOrders',
      content: childOrders?.length ? <ActivityFeedChildOrders childOrders={childOrders} /> : null,
    },
  ].filter(({ content }) => Boolean(content));

  const metadata = metadataSegments.length ? (
    <>
      {metadataSegments.map(({ key, content }, position) => (
        <Fragment key={key}>
          {position > 0 && METADATA_SEPARATOR}
          {content}
        </Fragment>
      ))}
    </>
  ) : undefined;

  const snapshotFilename = `${translate(LangEnum.ORDER_HISTORIC_FILENAME, {
    sufix: resolveActivityFeedOrderCode(row),
  })}_${formatDate(row.occurredAt)}`;

  const buildAction = () => {
    if (snapshotKind) {
      return (
        <ActivityFeedSnapshotDownload
          eventId={row.id}
          snapshotKind={snapshotKind}
          filename={snapshotFilename}
          testId={snapshotTestId}
        />
      );
    }

    if (phrase.action) {
      return (
        <ActivityFeedTrackingLink
          label={phrase.action.key}
          href={phrase.action.href}
          testId={trackingTestId}
        />
      );
    }

    return undefined;
  };

  const feedItem = (
    <FeedItem
      testId={testId}
      phrase={
        <>
          <ActivityFeedPhrase phrase={phrase} />
          <Typography component="span" variant="inherit" color="text.secondary">
            {` · ${formatTime(row.occurredAt)}`}
          </Typography>
        </>
      }
      metadata={metadata}
      action={buildAction()}
    />
  );

  if (!phrase.tooltip) {
    return feedItem;
  }

  return (
    <Tooltip
      fullWidth
      tabIndex={-1}
      TooltipContent={<ActivityFeedShipmentTooltip tooltip={phrase.tooltip} />}
    >
      {feedItem}
    </Tooltip>
  );
};

export default ActivityFeedRow;
